import { useEffect } from "react";
import { Link } from "react-router-dom";

import { Footer } from "@/components/landing/Footer";
import { trackEvent } from "@/lib/analytics";

const sections = [
  {
    number: "1.",
    title: "Information We Collect",
    paragraphs: [
      "Depending on how you use Quantify, we may collect:",
      "Information you provide to us",
    ],
    items: [
      "Your name",
      "Age",
      "Gender",
      "Height and weight",
      "Fitness or weight-loss goals",
      "Food and meal information you send us",
      "Photos of food you send for analysis",
      "Exercise and workout information",
      "Water and other habit-related information",
      "Questions or messages you send to Quantify",
      "Any other information you voluntarily provide while using the service",
    ],
    trailingParagraphs: [
      "Information collected automatically",
      "We may also collect basic technical and usage information, such as:",
    ],
    trailingItems: [
      "Device and browser information",
      "Website activity",
      "Usage of Quantify's features",
      "Dates and times of interactions with our service",
    ],
  },
  {
    number: "2.",
    title: "Why We Collect This Information",
    paragraphs: [
      "We collect your information primarily to provide and personalize Quantify's services.",
      "For example, we may use your information to:",
    ],
    items: [
      "Log and organize the food, exercise, weight and habits you provide",
      "Estimate calories and nutritional information",
      "Track your progress over time",
      "Provide personalized insights and recommendations",
      "Answer questions about your nutrition and activity",
      "Generate daily or periodic summaries",
      "Improve the accuracy and usefulness of our services",
      "Understand how people use Quantify and improve the product",
      "Prevent misuse, fraud, or abuse of our services",
      "Communicate with you about your account or service",
    ],
    trailingParagraphs: [
      "Your food, weight, exercise and other health-related information is used to provide the tracking and personalization features you request.",
    ],
  },
  {
    number: "3.",
    title: "WhatsApp and Your Messages",
    paragraphs: [
      "Quantify uses WhatsApp as one of its ways to communicate with you.",
      "When you send a message, photo, or other information to Quantify through WhatsApp, that information may be processed by us so that we can provide the requested service.",
      "WhatsApp also processes information according to its own privacy policies and terms.",
    ],
  },
  {
    number: "4.",
    title: "How We Use AI",
    paragraphs: [
      "Quantify uses artificial intelligence to help understand your messages, analyze meals, estimate nutritional information, answer questions and provide personalized insights.",
      "Your information may therefore be processed by third-party technology providers that help us operate these AI features.",
      "We use these providers only to support the services we provide and take reasonable steps to protect your information.",
    ],
  },
  {
    number: "5.",
    title: "We Don't Sell Your Personal Information",
    paragraphs: [
      "We do not sell your personal information to third parties.",
      "We may share information with trusted service providers who help us operate Quantify, such as hosting, analytics, payment, messaging and AI infrastructure providers.",
      "These providers receive only the information reasonably necessary to provide their services.",
    ],
  },
  {
    number: "6.",
    title: "Payments",
    paragraphs: [
      "If you purchase a Quantify subscription, payments may be processed through third-party payment providers.",
      "We do not store your complete card or banking details. Payment providers process payment information according to their own privacy policies and security practices.",
    ],
  },
  {
    number: "7.",
    title: "Data Security",
    paragraphs: [
      "We take reasonable technical and organizational measures to protect your information from unauthorized access, loss, misuse or disclosure.",
      "However, no internet-based service can guarantee complete security.",
    ],
  },
  {
    number: "8.",
    title: "How Long We Keep Your Data",
    paragraphs: [
      "We retain your information for as long as reasonably necessary to provide Quantify's services, maintain your account, comply with legal obligations, resolve disputes and enforce our agreements.",
      "You may request deletion of your personal information, subject to information we may be legally required to retain.",
    ],
  },
  {
    number: "9.",
    title: "Your Choices and Rights",
    paragraphs: [
      "Depending on applicable law, you may have the right to:",
    ],
    items: [
      "Request access to the personal information we hold about you",
      "Request correction of inaccurate information",
      "Request deletion of your information",
      "Withdraw consent where applicable",
      "Ask questions about how your information is being used",
    ],
    trailingParagraphs: [
      "To make a privacy-related request, contact us at:",
    ],
  },
  {
    number: "10.",
    title: "Age Requirement",
    paragraphs: [
      "Quantify is intended only for individuals aged 18 years and above.",
      "We do not knowingly provide our services to or collect personal information from individuals under 18.",
      "If we become aware that we have collected information from someone under 18, we will take reasonable steps to delete that information.",
    ],
  },
  {
    number: "11.",
    title: "Changes to This Policy",
    paragraphs: [
      "We may update this Privacy Policy from time to time as Quantify evolves. Any changes will be posted on this page with an updated \"Last updated\" date.",
    ],
  },
  {
    number: "12.",
    title: "Contact Us",
    paragraphs: [
      "If you have questions about this Privacy Policy or how Quantify handles your information, contact us:",
    ],
  },
];

const BulletList = ({ items, testId }) => (
  <ul className="space-y-3 text-sm text-[#171717]/80 md:text-base" data-testid={testId}>
    {items.map((item) => (
      <li key={item} className="flex gap-3 leading-relaxed">
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#176B4D]" aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    trackEvent("privacy_policy_page_view");
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F8F7F3] text-[#171717]" data-testid="privacy-policy-page">
      <main className="relative isolate px-6 pb-20 pt-8 md:px-12 md:pb-24 md:pt-12">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[420px] bg-[radial-gradient(circle_at_top_left,_rgba(184,217,154,0.5),_transparent_45%),radial-gradient(circle_at_top_right,_rgba(23,107,77,0.12),_transparent_30%)]" />

        <div className="mx-auto flex w-full max-w-[980px] flex-col gap-8 md:gap-10">
          <Link
            to="/"
            data-testid="privacy-policy-back-home-link"
            className="inline-flex w-fit items-center gap-2 rounded-full border border-[#171717]/12 bg-white/70 px-4 py-2 text-sm font-medium text-[#171717] transition-[border-color,background-color,transform] duration-300 ease-out hover:border-[#171717]/25 hover:bg-white hover:-translate-y-0.5"
          >
            <span aria-hidden="true">←</span>
            Back to home
          </Link>

          <section
            className="rounded-[2rem] border border-[#171717]/10 bg-white/80 px-6 py-8 shadow-[0_20px_80px_rgba(23,23,23,0.06)] md:px-10 md:py-12"
            data-testid="privacy-policy-hero"
          >
            <div className="mb-5 flex items-center gap-3 text-[#171717]/45" data-testid="privacy-policy-label">
              <span className="h-px w-8 bg-[#171717]/20" />
              <span className="text-xs font-semibold uppercase tracking-[0.18em]">Legal</span>
            </div>

            <h1 className="font-heading text-4xl font-semibold tracking-tight text-[#171717] sm:text-5xl lg:text-6xl" data-testid="privacy-policy-title">
              Privacy Policy
            </h1>

            <p className="mt-4 text-sm text-[#171717]/55 md:text-base" data-testid="privacy-policy-last-updated">
              Last updated: September 2026
            </p>

            <p className="mt-8 max-w-[720px] text-sm leading-relaxed text-[#171717]/78 md:text-base" data-testid="privacy-policy-intro">
              At Quantify, we believe tracking your health should be simple and personal. This Privacy Policy explains what information we collect, why we collect it, and how we use it when you use Quantify through WhatsApp, our website, or other services.
            </p>
          </section>

          <section className="grid gap-5" data-testid="privacy-policy-sections">
            {sections.map((section) => (
              <article
                key={section.number}
                className="rounded-[1.75rem] border border-[#171717]/9 bg-[#F8F7F3]/80 px-6 py-6 shadow-[0_12px_40px_rgba(23,23,23,0.04)] md:px-8 md:py-7"
                data-testid={`privacy-policy-section-${section.number.replace(/\./g, "")}`}
              >
                <div className="flex flex-col gap-4">
                  <h2 className="font-heading text-2xl font-semibold tracking-tight text-[#171717] md:text-[2rem]" data-testid={`privacy-policy-section-title-${section.number.replace(/\./g, "")}`}>
                    {section.number} {section.title}
                  </h2>

                  <div className="space-y-4">
                    {section.paragraphs?.map((paragraph) => (
                      <p key={paragraph} className="text-sm leading-relaxed text-[#171717]/80 md:text-base">
                        {paragraph}
                      </p>
                    ))}

                    {section.items?.length ? (
                      <BulletList
                        items={section.items}
                        testId={`privacy-policy-section-list-${section.number.replace(/\./g, "")}`}
                      />
                    ) : null}

                    {section.trailingParagraphs?.map((paragraph) => (
                      <p key={paragraph} className="text-sm leading-relaxed text-[#171717]/80 md:text-base">
                        {paragraph}
                      </p>
                    ))}

                    {section.trailingItems?.length ? (
                      <BulletList
                        items={section.trailingItems}
                        testId={`privacy-policy-section-trailing-list-${section.number.replace(/\./g, "")}`}
                      />
                    ) : null}

                    {section.title === "Contact Us" ? (
                      <div className="rounded-[1.2rem] border border-[#176B4D]/15 bg-[#B8D99A]/18 p-4 text-sm text-[#171717]/82 md:text-base" data-testid="privacy-policy-contact-card">
                        <p>Quantify</p>
                        <p>
                          Email:{" "}
                          <a
                            href="mailto:support@quantifyyourhealth.com"
                            className="font-medium text-[#176B4D] underline decoration-[#176B4D]/35 underline-offset-4"
                            data-testid="privacy-policy-contact-email-link"
                          >
                            support@quantifyyourhealth.com
                          </a>
                        </p>
                        <p>
                          Website:{" "}
                          <a
                            href="https://www.quantifyyourhealth.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-[#176B4D] underline decoration-[#176B4D]/35 underline-offset-4"
                            data-testid="privacy-policy-contact-website-link"
                          >
                            www.quantifyyourhealth.com
                          </a>
                        </p>
                      </div>
                    ) : null}

                    {section.title === "Your Choices and Rights" ? (
                      <a
                        href="mailto:support@quantifyyourhealth.com"
                        className="inline-flex w-fit font-medium text-[#176B4D] underline decoration-[#176B4D]/35 underline-offset-4"
                        data-testid="privacy-policy-rights-email-link"
                      >
                        support@quantifyyourhealth.com
                      </a>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}